/**
 * 运行轨迹抽屉（O-1；PR5 升级，判定卡 03、线框 01）：外层 `ui/Drawer`（不手搭 Radix，S7），里面三栏
 * ① 对话 ｜ ② 轨迹（按层 ＋ 按事项分组）｜ ③ 详情（分段控件切类型）。
 * 抽屉头一行：会话标题 · 模式 Plan · 快照号（点开配置版本）· 计划版本。
 * 运行记录打开它；人工提问把同一套三栏（`PlanTraceColumns`）嵌在页面里、左栏底部带输入框。
 * 对话栏点前几轮 → 按那一轮自己的运行记录编号再拉 A-22（对话里每轮都带编号）。
 * 盲测会话（A-22 回 `sealed: true`，10-21 A1）：不画三栏，只写「盲测题，内容不展示」。
 */
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Drawer } from "@/components/ui/drawer";
import { LoadingInline } from "@/components/ui/feedback";
import {
	type PlanRecordDetail,
	planErrorMessage,
	useCatalog,
	usePlanRecordDetail,
} from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";
import { useDemandLabel } from "../kit/demand-label";
import { FailState } from "../kit/fail-state";
import { RESUME_ACTION_ZH, SOURCE_TONE, SOURCE_ZH, STOP_REASON_ZH, zh } from "../kit/plan-labels";
import { SealedNotice } from "../kit/sealed-notice";
import { SnapshotTag } from "../kit/snapshot-tag";
import { ChatColumn, type DrawerTurn, InfoChip } from "./execution-drawer";
import { TraceColumn, type TraceSel } from "./trace-column";
import { TraceDetail } from "./trace-detail";
import { attrs, buildTraceModel, op } from "./trace-model";

/** 当前那一轮的轨迹数据（人工提问：本轮执行中 ／ 拉详情中 ／ 拉失败；运行记录：按轮拉） */
export type FocusTrace = {
	detail: PlanRecordDetail | null | undefined;
	loading?: boolean;
	error?: string | null;
	onRetry?: () => void;
	retrying?: boolean;
	pending?: boolean;
};

/** 三栏（抽屉与人工提问共用） */
export function PlanTraceColumns({
	turns,
	focus,
	onFocus,
	trace,
	customerId,
	chatFooter,
	chatEmptyHint,
	className,
}: {
	turns: DrawerTurn[];
	focus: number;
	onFocus: (i: number) => void;
	trace: FocusTrace;
	customerId?: string;
	chatFooter?: ReactNode;
	chatEmptyHint?: ReactNode;
	className?: string;
}) {
	const cat = useCatalog();
	// 客户列表没加载出来只少「背景」信息块里的客户名，不挡执行视图
	const customerName = cat.isError
		? undefined
		: cat.data?.seed.customers.find((c) => c.customer_id === customerId)?.name;
	const detail = trace.detail ?? null;
	const demandLabel = useDemandLabel();
	const model = useMemo(
		() => (detail ? buildTraceModel(detail, demandLabel) : null),
		[detail, demandLabel],
	);
	const [sel, setSel] = useState<TraceSel>(null);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const modelKey = detail?.record.record_id ?? "";
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换了一轮（另一条记录）才重置选中与展开
	useEffect(() => {
		setSel(null);
		setExpanded(new Set(model?.currentKey ? [model.currentKey] : []));
	}, [modelKey]);
	const idx = Math.min(focus, Math.max(0, turns.length - 1));
	return (
		<div
			className={cn(
				"grid h-full min-h-0 grid-cols-[280px_minmax(0,1.15fr)_minmax(0,1fr)] grid-rows-1 divide-x divide-surface-border overflow-hidden",
				className,
			)}
		>
			<ChatColumn
				turns={turns}
				focus={idx}
				onFocus={onFocus}
				footer={chatFooter}
				emptyHint={chatEmptyHint}
				topExtra={
					customerId ? (
						<InfoChip
							label={`背景 · 客户 ${customerName ?? customerId}`}
							tone="muted"
							active={sel?.kind === "bg"}
							onClick={() => setSel({ kind: "bg" })}
						/>
					) : null
				}
			/>
			<TraceColumn
				turnNo={idx + 1}
				model={model}
				loading={trace.loading}
				error={trace.error}
				onRetry={trace.onRetry}
				retrying={trace.retrying}
				pending={trace.pending}
				expanded={expanded}
				onToggle={(k) =>
					setExpanded((s) => {
						const n = new Set(s);
						if (n.has(k)) n.delete(k);
						else n.add(k);
						return n;
					})
				}
				selected={sel}
				onSelect={setSel}
			/>
			<TraceDetail sel={sel} model={model} detail={detail} customerId={customerId} />
		</div>
	);
}

/** 一轮的对话气泡徽标：接续动作 ／ 已执行（拉到这一轮的事件才有） */
export function turnBadges(
	d: PlanRecordDetail | null | undefined,
): Pick<DrawerTurn, "resume" | "committed"> {
	if (!d) return {};
	const sig = d.tree.find((e) => op(e) === "plan.signal");
	const act = sig ? String(attrs(sig).resume_action ?? "") : "";
	return {
		resume: act && act !== "none" && !attrs(sig).first_turn ? zh(RESUME_ACTION_ZH, act) : undefined,
		committed: d.tree.some((e) => op(e) === "execute_tool" && attrs(e).write_class === "committed"),
	};
}

/** 运行记录 → 运行轨迹抽屉（A-22；整段会话，点哪一轮拉哪一轮） */
export function PlanTraceDrawer({
	recordId,
	onClose,
	topStrip,
	titlePrefix,
}: {
	recordId: string | null;
	onClose: () => void;
	/** 三栏上方的一条（PR9：评测报告点开时放评分卡条） */
	topStrip?: ReactNode;
	/** 标题前缀（PR9：评测报告点开时写题号 · 第几次） */
	titlePrefix?: string;
}) {
	const q = usePlanRecordDetail(recordId);
	const d = q.data;
	const dialogue = d?.dialogue ?? [];
	const curIdx = Math.max(
		0,
		dialogue.findIndex((t) => t.record_id === d?.record.record_id),
	);
	const [focus, setFocus] = useState<number | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换一条记录打开时回到它自己那一轮
	useEffect(() => setFocus(null), [recordId]);
	const idx = focus ?? curIdx;
	const focusId = dialogue[idx]?.record_id ?? null;
	const other = usePlanRecordDetail(focusId && focusId !== d?.record.record_id ? focusId : null);
	const focusDetail = focusId === d?.record.record_id ? d : other.data;
	if (!recordId) return null;

	const turns: DrawerTurn[] = dialogue.map((t, i) => ({
		msg: t.message,
		reply: t.reply,
		stop: t.stop_reason ? zh(STOP_REASON_ZH, t.stop_reason) : undefined,
		...(i === idx ? turnBadges(focusDetail) : {}),
	}));
	const planV = d?.header.plan_version ?? d?.record.plan_version;
	let body: ReactNode;
	if (q.isLoading) body = <LoadingInline text="加载这条记录的轨迹…" />;
	else if (q.isError || !d)
		body = (
			<FailState
				testId="plan-trace-fail"
				title="没取到这条记录的轨迹"
				reason={planErrorMessage(q.error)}
				onRetry={() => q.refetch()}
				retrying={q.isFetching}
			/>
		);
	else if (d.sealed) body = <SealedNotice />;
	else
		body = (
			<PlanTraceColumns
				className={topStrip ? "min-h-0 flex-1" : undefined}
				turns={turns}
				focus={idx}
				onFocus={setFocus}
				customerId={d.header.customer_id ?? undefined}
				trace={
					focusId === d.record.record_id
						? { detail: d }
						: {
								detail: other.data,
								loading: other.isLoading,
								error: other.isError ? planErrorMessage(other.error) : null,
								onRetry: () => other.refetch(),
								retrying: other.isFetching,
							}
				}
			/>
		);
	return (
		<Drawer
			open
			onClose={onClose}
			testId="plan-trace-drawer"
			flush
			title={
				(titlePrefix ? `${titlePrefix} · ` : "") +
				(q.isError ? "加载失败" : d ? `会话 · 「${d.header.title}」` : "运行轨迹 · 加载中…")
			}
			badges={
				d ? (
					<span className="flex flex-wrap items-center gap-1.5" data-testid="plan-trace-head">
						<Badge tone="neutral">模式 Plan</Badge>
						<Badge tone={SOURCE_TONE[d.record.source] ?? "muted"}>
							{zh(SOURCE_ZH, d.record.source)}
						</Badge>
						<span className="flex items-center gap-1 text-xs text-text-muted">
							快照 <SnapshotTag id={d.header.snapshot_id} />
						</span>
						<Badge tone="muted">计划 {planV ? `v${planV}` : "—"}</Badge>
					</span>
				) : null
			}
		>
			{topStrip ? (
				<div className="flex h-full min-h-0 flex-col">
					{topStrip}
					{body}
				</div>
			) : (
				body
			)}
		</Drawer>
	);
}
